import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  Renderer2
} from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, from, map, startWith } from 'rxjs';
import { FrontendType, PermissionResource } from '@coin/shared/core/util-enums';
import { AuthService } from '@coin/shared/auth/data-access';
import { DynamicDomAccessDirective } from '../../data/dynamic-dom-access.directive';
import { PermissionsDirective } from '../../data/permissions.directive';
import { Admin } from '@coin/shared/core/util-helpers';
import { SimplebarAngularModule } from 'simplebar-angular';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AsyncPipe } from '@angular/common';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';
import { KeyboardMenuNavigationDirective } from '@coin/shared/core/util-directives';
import { CoinAppComponent } from './components/coin-app/coin-app.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserState } from '../../data/user.state';
import { PermissionsService } from '../../data/permissions.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'coin-siemens-energy-coin-apps-overview',
  templateUrl: './coin-apps-overview.component.html',
  styleUrls: ['./coin-apps-overview.component.scss'],
  imports: [
    SimplebarAngularModule,
    CoinAppComponent,
    TranslateModule,
    MatTooltipModule,
    DynamicDomAccessDirective,
    AsyncPipe,
    FeatureFlagDirective,
    PermissionsDirective,
    KeyboardMenuNavigationDirective
  ]
})
export class CoinAppsOverviewComponent {
  private router = inject(Router);
  private userState = inject(UserState);
  private authService = inject(AuthService);
  private permissionsService = inject(PermissionsService);

  coinBaseUrl = input('', { transform: (url: string) => this.removeTrailingSlash(url) });
  cosmosBaseUrl = input('', { transform: (url: string) => this.removeTrailingSlash(url) });

  public readonly isLineManager = input.required<boolean>();
  public readonly isHead = input.required<boolean>();

  public permissionResource = PermissionResource;
  public appType = FrontendType;

  public isMercerAccessAllowed$ = combineLatest([
    this.userState.allPermissions$,
    this.userState.isDynamicMercerAccessAllowed$
  ]).pipe(
    map(([permissions, isDynamicMercerAccessAllowed]) => {
      return isDynamicMercerAccessAllowed || this.permissionsService.isSuperAdmin();
    })
  );

  public analyticsUrl = 'https://siemens-energy.pulse.cloud/project/2';
  public isDeputy = toSignal(
    from(this.authService.getEmulationDecoded()).pipe(
      map(a => a?.IsDeputy ?? false),
      startWith(false)
    )
  );

  appsClosed = output();

  constructor() {
    const renderer = inject(Renderer2);
    const element = inject(ElementRef);

    afterNextRender(() => {
      const cw = element.nativeElement.querySelector('.simplebar-content-wrapper');
      if (cw) {
        renderer.setAttribute(cw, 'tabindex', '-1');
      }
    });
  }

  public openApp(link: string, applicationType: FrontendType = FrontendType.customer): void {
    if (applicationType === FrontendType.customer && this.coinBaseUrl()) {
      this.openPage(`${this.coinBaseUrl()}${link}`);
    } else if (applicationType === FrontendType.cosmos && this.cosmosBaseUrl()) {
      this.openPage(`${this.cosmosBaseUrl()}${link}`);
    } else {
      this.router.navigate([link]);
    }
  }

  public openPage(url: string): void {
    window.open(url, '_blank', 'noopener noreferrer');
  }

  public openAdminFrontend(): void {
    Admin.open('HomePageModule');
  }

  private removeTrailingSlash(value: string): string {
    return value.replace(/\/$/, '');
  }
}
